import { useState } from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { PartialInvoiceDialog } from '@/pages/jobs/components/billing/PartialInvoiceDialog';
import { FinalInvoiceDialog } from '@/pages/jobs/components/billing/FinalInvoiceDialog';
import type { ToBillRow } from '@jobos/shared';

/**
 * Stage 1's primary action.
 *
 * It opens the EXISTING PartialInvoiceDialog and FinalInvoiceDialog unchanged
 * (FR-2.4). This module does not introduce a second way to build an invoice —
 * a forked dialog would be a second place for the line-item picker, the
 * void-and-reissue conflict prompt and the cash-partial rules to drift.
 *
 * Both dialogs take exactly { open, onOpenChange, jobId, jobCode,
 * jobDescription } and fetch everything else themselves, and ToBillRow already
 * carries all three data props — which is what makes the reuse real rather
 * than aspirational.
 */
export interface CreateInvoiceMenuProps {
  row: ToBillRow;
  /** The workspace invalidates its own counts and rows from here. */
  onCreated: () => void;
}

export function CreateInvoiceMenu({ row, onCreated }: CreateInvoiceMenuProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [partialOpen, setPartialOpen] = useState(false);
  const [finalOpen, setFinalOpen] = useState(false);

  const dialogProps = {
    jobId: row.jobId,
    jobCode: row.jobCode,
    jobDescription: row.jobDescription,
    onCreated,
  };

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            className="bg-blue-900 text-white hover:bg-blue-800"
            aria-label={`Create invoice for ${row.jobCode}`}
          >
            <Plus className="mr-1 h-4 w-4" />
            Create invoice
            <ChevronDown className="ml-1 h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" role="menu">
          <DropdownMenuItem
            role="menuitem"
            onClick={() => {
              setMenuOpen(false);
              setFinalOpen(true);
            }}
          >
            Final invoice
          </DropdownMenuItem>
          <DropdownMenuItem
            role="menuitem"
            onClick={() => {
              setMenuOpen(false);
              setPartialOpen(true);
            }}
          >
            Partial invoice
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Mounted only while open: each dialog fetches the job's breakdown and
          invoices on mount, and stage 1 can be a hundred rows long. */}
      {finalOpen && (
        <FinalInvoiceDialog open onOpenChange={setFinalOpen} {...dialogProps} />
      )}
      {partialOpen && (
        <PartialInvoiceDialog open onOpenChange={setPartialOpen} {...dialogProps} />
      )}
    </>
  );
}
